import React, { useState } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

const format = (val) =>
  `₱${(val || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

const percentDiff = (prev, curr) => {
  if (prev === 0) return "—";
  const diff = ((curr - prev) / prev) * 100;
  const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
  const color = diff > 0 ? "green" : diff < 0 ? "red" : "gray";
  return (
    <span style={{ color }}>
      {arrow} {Math.abs(diff).toFixed(2)}%
    </span>
  );
};

const SalesStatistics = ({ data }) => {
  const [salesType, setSalesType] = useState("gross");
  const statistics = data?.statistics;
  if (!statistics) return null;

  const stats = statistics;

  const revenueValue =
    salesType === "gross"
      ? {
          previous: stats.previous.grossSales,
          current: stats.current.grossSales,
        }
      : {
          previous: stats.previous.netSalesWithoutVat,
          current: stats.current.netSalesWithoutVat,
        };

  const expensePrevious =
    (stats.previous.inventoryRestock || 0) +
    (stats.previous.inventoryUsed || 0);
  const expenseCurrent =
    (stats.current.inventoryRestock || 0) + (stats.current.inventoryUsed || 0);

  const grandPrevious = revenueValue.previous - expensePrevious;
  const grandCurrent = revenueValue.current - expenseCurrent;

  const salesRows = [
    {
      label: "Gross Sales",
      previous: stats.previous.grossSales,
      current: stats.current.grossSales,
    },
    {
      label: "Net Sales (w/o VAT)",
      previous: stats.previous.netSalesWithoutVat,
      current: stats.current.netSalesWithoutVat,
    },
    {
      label: "Discounts Given",
      previous: stats.previous.totalDiscount,
      current: stats.current.totalDiscount,
    },
    {
      label: "Net Sales (after Discount)",
      previous: stats.previous.netSalesWithDiscounts,
      current: stats.current.netSalesWithDiscounts,
    },
    {
      label: "Service Charge",
      previous: stats.previous.totalServiceCharge || 0,
      current: stats.current.totalServiceCharge || 0,
    },
    // {
    //   label: "Inventory Restock",
    //   previous: stats.previous.inventoryRestock || 0,
    //   current: stats.current.inventoryRestock || 0,
    // },
    // {
    //   label: "Inventory Used (Food Cost)",
    //   previous: stats.previous.inventoryUsed || 0,
    //   current: stats.current.inventoryUsed || 0,
    // },
    // {
    //   label: "Final Gross Total (after subtraction)",
    //   previous:
    //     stats.previous.grossSales -
    //     ((stats.previous.inventoryRestock || 0) +
    //       (stats.previous.inventoryUsed || 0)),
    //   current:
    //     stats.current.grossSales -
    //     ((stats.current.inventoryRestock || 0) +
    //       (stats.current.inventoryUsed || 0)),
    // },
    // {
    //   label: "Final Net Total (w/o VAT after subtraction)",
    //   previous:
    //     stats.previous.netSalesWithoutVat -
    //     ((stats.previous.inventoryRestock || 0) +
    //       (stats.previous.inventoryUsed || 0)),
    //   current:
    //     stats.current.netSalesWithoutVat -
    //     ((stats.current.inventoryRestock || 0) +
    //       (stats.current.inventoryUsed || 0)),
    // },
  ];

  const receiptTypeRows = [
    {
      label: "Dine-In Qty",
      previous: stats.previous.dineInQty,
      current: stats.current.dineInQty,
    },
    {
      label: "Take-Out Qty",
      previous: stats.previous.takeOutQty,
      current: stats.current.takeOutQty,
    },
    {
      label: "Delivery Qty",
      previous: stats.previous.deliveryQty,
      current: stats.current.deliveryQty,
    },
    {
      label: "Dine-In Amount (₱)",
      previous: stats.previous.dineInAmount,
      current: stats.current.dineInAmount,
    },
    {
      label: "Take-Out Amount (₱)",
      previous: stats.previous.takeOutAmount,
      current: stats.current.takeOutAmount,
    },
    {
      label: "Delivery Amount (₱)",
      previous: stats.previous.deliveryAmount,
      current: stats.current.deliveryAmount,
    },
  ];

  const paymentTypeRows = [];
  const currentPayments = stats.current.paymentSummary || {};
  const previousPayments = stats.previous.paymentSummary || {};

  const appendPaymentRow = (
    label,
    prevData = {},
    currData = {},
    isChild = false
  ) => {
    paymentTypeRows.push({
      label: isChild ? `↳ ${label}` : label,
      previousQty: prevData.quantity || 0,
      previousAmt: prevData.amount || 0,
      currentQty: currData.quantity || 0,
      currentAmt: currData.amount || 0,
    });
  };

  const allKeys = new Set([
    ...Object.keys(previousPayments),
    ...Object.keys(currentPayments),
  ]);

  const sortedKeys = [...allKeys]
    .filter((k) => k !== "Cash" && k !== "E-Cash")
    .sort((a, b) => {
      const currA = currentPayments[a]?.amount || 0;
      const currB = currentPayments[b]?.amount || 0;
      return currB - currA;
    });

  const finalKeys = ["Cash", "E-Cash", ...sortedKeys];

  for (const key of finalKeys) {
    const prev = previousPayments[key] || {};
    const curr = currentPayments[key] || {};
    appendPaymentRow(key, prev, curr);

    if (key === "E-Cash") {
      const allSubKeys = new Set([
        ...Object.keys(prev.children || {}),
        ...Object.keys(curr.children || {}),
      ]);

      const sortedSubKeys = [...allSubKeys].sort((a, b) => {
        const valA = curr.children?.[a]?.amount || 0;
        const valB = curr.children?.[b]?.amount || 0;
        return valB - valA;
      });

      for (const sub of sortedSubKeys) {
        appendPaymentRow(sub, prev.children?.[sub], curr.children?.[sub], true);
      }
    }
  }

  return (
    <Box sx={{ mb: 5, maxWidth: 900, margin: "0 auto" }}>
      <Typography variant="h5" align="center" gutterBottom>
        Sales Summary
      </Typography>
      <Typography variant="subtitle2" align="center" gutterBottom>
        <strong>Previous Range:</strong> {stats.previousRange.start} to{" "}
        {stats.previousRange.end}
        &nbsp; | &nbsp;
        <strong>Current Range:</strong> {stats.currentRange.start} to{" "}
        {stats.currentRange.end}
      </Typography>

      {/* Financial Summary */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "65%" }}>
                <strong>Sales</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Previous</strong>
              </TableCell>
              <TableCell align="center"></TableCell>
              <TableCell align="right">
                <strong>Current</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {salesRows.map((row) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{format(row.previous)}</TableCell>
                <TableCell align="center">
                  {percentDiff(row.previous, row.current)}
                </TableCell>
                <TableCell align="right">{format(row.current)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Transaction Type Summary */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "65%" }}>
                <strong>Transactions</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Previous</strong>
              </TableCell>
              <TableCell align="center"></TableCell>
              <TableCell align="right">
                <strong>Current</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Total Transactions</TableCell>
              <TableCell align="right">
                {stats.previous.totalTransactions}
              </TableCell>
              <TableCell align="center">
                {percentDiff(
                  stats.previous.totalTransactions,
                  stats.current.totalTransactions
                )}
              </TableCell>
              <TableCell align="right">
                {stats.current.totalTransactions}
              </TableCell>
            </TableRow>
            {receiptTypeRows.map((row) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">
                  {row.label.toLowerCase().includes("qty")
                    ? row.previous
                    : format(row.previous)}
                </TableCell>
                <TableCell align="center">
                  {percentDiff(row.previous, row.current)}
                </TableCell>
                <TableCell align="right">
                  {row.label.toLowerCase().includes("qty")
                    ? row.current
                    : format(row.current)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Payment Summary */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Payment Type</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Previous Qty</strong>
              </TableCell>
              <TableCell align="center"></TableCell>
              <TableCell align="right">
                <strong>Current Qty</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Previous Amount</strong>
              </TableCell>
              <TableCell align="center"></TableCell>
              <TableCell align="right">
                <strong>Current Amount</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentTypeRows.map((row) => {
              const diffQty = (prev, curr) => {
                if (prev === 0) return "—";
                const diff = ((curr - prev) / prev) * 100;
                const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
                const color = diff > 0 ? "green" : diff < 0 ? "red" : "gray";
                return (
                  <span style={{ color }}>
                    {arrow} {Math.abs(diff).toFixed(2)}%
                  </span>
                );
              };

              return (
                <TableRow key={row.label}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell align="right">{row.previousQty}</TableCell>
                  <TableCell align="center">
                    {diffQty(row.previousQty, row.currentQty)}
                  </TableCell>
                  <TableCell align="right">{row.currentQty}</TableCell>
                  <TableCell align="right">{format(row.previousAmt)}</TableCell>
                  <TableCell align="center">
                    {diffQty(row.previousAmt, row.currentAmt)}
                  </TableCell>
                  <TableCell align="right">{format(row.currentAmt)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Totals Summary */}
      <Typography variant="h6" gutterBottom align="center" sx={{ mt: 3 }}>
        Total Summary
      </Typography>
      <Typography variant="subtitle2" align="center" gutterBottom>
        <strong>Previous Range:</strong> {stats.previousRange.start} to{" "}
        {stats.previousRange.end}
        &nbsp; | &nbsp;
        <strong>Current Range:</strong> {stats.currentRange.start} to{" "}
        {stats.currentRange.end}
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "65%" }}>
                <FormControl size="small">
                  <Select
                    value={salesType}
                    onChange={(e) => setSalesType(e.target.value)}
                  >
                    <MenuItem value="gross">Total Gross Sales</MenuItem>
                    <MenuItem value="net">Total Net Sales (w/o VAT)</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell align="right">
                <strong>Previous</strong>
              </TableCell>
              <TableCell align="center"></TableCell>
              <TableCell align="right">
                <strong>Current</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <span style={{ color: "green" }}>
                  {salesType === "gross"
                    ? "Gross Sales"
                    : "Net Sales (w/o VAT)"}
                </span>
              </TableCell>
              <TableCell align="right" style={{ color: "green" }}>
                {format(revenueValue.previous)}
              </TableCell>
              <TableCell align="center">
                {percentDiff(revenueValue.previous, revenueValue.current)}
              </TableCell>
              <TableCell align="right" style={{ color: "green" }}>
                {format(revenueValue.current)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span style={{ color: "red" }}>Total Expense</span>
              </TableCell>
              <TableCell align="right" style={{ color: "red" }}>
                {format(expensePrevious)}
              </TableCell>
              <TableCell align="center">
                {percentDiff(expensePrevious, expenseCurrent)}
              </TableCell>
              <TableCell align="right" style={{ color: "red" }}>
                {format(expenseCurrent)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Grand Total</strong>
              </TableCell>
              <TableCell
                align="right"
                style={{ color: grandPrevious < 0 ? "red" : "green" }}
              >
                {format(grandPrevious)}
              </TableCell>
              <TableCell align="center">
                {percentDiff(grandPrevious, grandCurrent)}
              </TableCell>
              <TableCell
                align="right"
                style={{ color: grandCurrent < 0 ? "red" : "green" }}
              >
                {format(grandCurrent)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SalesStatistics;
